<template>
    <div class="image-preview-wrapper">
        <!-- 图片预览组 -->
        <div v-if="imageList.length" class="images-container" ref="imagesContainer">
            <!-- 单张图片展示 -->
            <img
                :width="width"
                :src="img"
                :data-preview-id="`${instanceUid}-${index}`"
                class="preview-image"
                v-for="(img, index) in imageList" :key="index"
                @error="handleImageError"
                @load="handleImageLoad"
                @click="handleImageClick"
            />
        </div>

        <!-- 无数据时的空状态 -->
        <a-empty v-else :image="simpleImage" description="暂无图片" />
    </div>
</template>

<script setup>
import { ref, computed, defineProps, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { Empty } from 'ant-design-vue';
import { useRoute } from 'vue-router';
import Viewer from 'viewerjs';
import 'viewerjs/dist/viewer.css';

// 接收父组件参数
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  width: {
    type: [Number, String],
    default: 100
  },
  preview: {
    type: Boolean,
    default: true
  },
  separator: {
    type: String,
    default: ';'
  }
});

// 空状态图片
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;

// refs
const imagesContainer = ref(null);
const route = useRoute();
const instance = getCurrentInstance();
const instanceUid = instance?.uid ?? 0;

// 处理图片数据逻辑
const imageList = computed(() => {
  if (Array.isArray(props.modelValue)) {
    return props.modelValue.map(item => item.url);
  }
  return props.modelValue
    ? props.modelValue
        .split(props.separator)
        .map(item => item.trim())
        .filter(Boolean)
    : [];
});

// Viewer.js 基础配置
const baseViewerOptions = {
  inline: false,
  button: true,
  navbar: true,
  title: true,
  toolbar: {
    zoomIn: 4,
    zoomOut: 4,
    oneToOne: 4,
    reset: 4,
    prev: 4,
    play: {
      show: 4,
      size: 'large',
    },
    next: 4,
    rotateLeft: 4,
    rotateRight: 4,
    flipHorizontal: 4,
    flipVertical: 4,
  },
  tooltip: true,
  movable: true,
  zoomable: true,
  rotatable: true,
  scalable: true,
  transition: true,
  fullscreen: true,
  keyboard: true,
  backdrop: true,
  loading: true,
  loop: true,
  interval: 5000,
  minZoomRatio: 0.01,
  maxZoomRatio: 100,
  zoomRatio: 0.5,
  zoomOnTouch: true,
  zoomOnWheel: true
};

// 图片加载成功处理
const handleImageLoad = () => {
  // 仅用于触发重新渲染，无需再为每个组件单独初始化 viewer
};

// 图片加载失败处理
const handleImageError = (event) => {
  console.error('图片加载失败:', event.target.src);
  event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmNWY1ZjUiLz4KPHR4dCB4PSI1MCIgeT0iNTAiIGZvbnQtZmFpbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5Yqg6L2955qF5aSx6LSlPC90eHQ+Cjwvc3ZnPgo=';
};

// 点击图片时使用当前弹框/容器内所有 preview-image 组成一个 viewer，实现跨表单切换
const handleImageClick = (event) => {
  const target = event.target;

  // 优先使用当前 ant-modal 弹框容器，否则回退到当前组件容器
  const modalContent = target.closest('.ant-modal-content');
  const sourceContainer = modalContent || imagesContainer.value;

  if (!sourceContainer) return;

  const allImages = Array.from(sourceContainer.querySelectorAll('.preview-image'));
  if (!allImages.length) return;

  const clickedId = target.getAttribute('data-preview-id');
  const index = allImages.findIndex(img => img.getAttribute('data-preview-id') === clickedId);

  // 创建临时容器承载所有图片，避免受其他 DOM 影响
  const tempContainer = document.createElement('div');
  tempContainer.style.display = 'none';
  allImages.forEach(img => {
    const clone = img.cloneNode();
    tempContainer.appendChild(clone);
  });
  document.body.appendChild(tempContainer);

  // 判断当前路由是否包含 personApplicationApproval
  const isPersonApprovalRoute = route.path.includes('personApplicationApproval') || route.path.includes('personShddFinalCheck');

  let viewer = null;

  function closeViewerOnClick() {
    if (viewer) {
      viewer.hide();
    }
  }

  const viewerOptions = {
    ...baseViewerOptions,
    initialViewIndex: index >= 0 ? index : 0,
    hidden: () => {
      if (viewer) {
        viewer.destroy();
        viewer = null;
      }
      if (tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
    }
  };

  // 只有在 personApplicationApproval 路由下才添加 viewed 事件
  if (isPersonApprovalRoute) {
    viewerOptions.viewed = (event) => {
      const img = event.detail.image;
      img.removeEventListener('click', closeViewerOnClick);
      img.addEventListener('click', closeViewerOnClick);
    };
  }

  viewer = new Viewer(tempContainer, viewerOptions);
  viewer.view(index >= 0 ? index : 0);
};

// 生命周期
onMounted(() => {
  // 无需再为每个组件单独初始化 viewer
});

onUnmounted(() => {
  // 临时 viewer 在 hidden 时已销毁，无需额外清理
});
</script>

<style scoped>
.image-preview-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.images-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.preview-image {
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  object-fit: cover;
  height: 100px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.preview-image:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
}

.image-placeholder {
  width: v-bind('props.width + "px"');
  height: v-bind('props.width + "px"');
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  color: #999;
}
</style>
